import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listConsumerCsTickets,
  openConsumerCsTicket,
  type ConsumerCsTicket,
  type OpenConsumerTicketInput,
} from '@/lib/api';
import { useTk } from '@/lib/i18n';
import { useAuth } from '@/lib/use-auth';
import { BottomNav } from '@/components/BottomNav';
import { Button } from '@sabay/ui-kit';

export function MyCustomerServicePage(): React.ReactElement {
  const tk = useTk();
  const navigate = useNavigate();
  const auth = useAuth();
  const [items, setItems] = useState<ConsumerCsTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deeplink, setDeeplink] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.token) {
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    listConsumerCsTickets(auth.token, 1, ac.signal)
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err: Error) => {
        if (ac.signal.aborted) return;
        setError(err.message);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [auth.status, auth.token]);

  async function onOpenTicket(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!auth.token) return;
    const fd = new FormData(e.currentTarget);
    const body: OpenConsumerTicketInput = {
      kind: fd.get('kind') as OpenConsumerTicketInput['kind'],
      subject: String(fd.get('subject') ?? ''),
      body: String(fd.get('body') ?? ''),
    };
    const oid = String(fd.get('orderId') ?? '').trim();
    const mid = String(fd.get('merchantId') ?? '').trim();
    if (oid) body.orderId = oid;
    if (mid) body.merchantId = mid;

    setSubmitting(true);
    setError(null);
    setDeeplink(null);
    try {
      const res = await openConsumerCsTicket(body, auth.token);
      setDeeplink(res.csBotDeeplink);
      const list = await listConsumerCsTickets(auth.token, 1);
      setItems(list.items);
      setTotal(list.total);
      e.currentTarget.reset();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen pb-24">
      <header className="sticky top-0 z-20 flex items-center gap-3 bg-clay-50/85 px-5 pb-3 pt-5 backdrop-blur-md">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(-1)}
          className="grid h-9 w-9 place-items-center rounded-full border border-clay-200 bg-white p-0 text-clay-700"
          aria-label="back"
        >
          <BackGlyph />
        </Button>
        <span className="eyebrow flex-1">{tk('customerService.title')}</span>
      </header>

      <div className="mt-2 space-y-6 px-5">
        {auth.status !== 'authenticated' ? (
          <div className="surface-card border-dashed p-8 text-center text-sm text-clay-500">
            {tk('me.unauth.body')}
          </div>
        ) : loading ? (
          <p className="py-6 text-center text-sm text-clay-400">{tk('common.loading')}</p>
        ) : (
          <>
            {error ? (
              <div className="rounded-2xl border border-danger-500/40 bg-danger-500/10 p-4 text-sm text-danger-500">
                {tk('common.error.generic')}
                <p className="mt-1 break-all text-xs opacity-80">{error}</p>
              </div>
            ) : null}

            {deeplink ? (
              <div className="surface-card space-y-2 p-4 text-sm text-clay-800">
                <p>{tk('customerService.deeplinkHint')}</p>
                <a
                  href={deeplink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block break-all text-amber-700 underline"
                >
                  {tk('customerService.botLink')}
                </a>
              </div>
            ) : null}

            <form onSubmit={(ev) => void onOpenTicket(ev)} className="surface-card space-y-3 p-4">
              <h2 className="text-sm font-semibold text-clay-900">{tk('customerService.newTicket')}</h2>
              <label className="block text-xs text-clay-600">
                {tk('customerService.kind')}
                <select
                  name="kind"
                  required
                  className="mt-1 w-full rounded-xl border border-clay-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="INQUIRY">INQUIRY</option>
                  <option value="COMPLAINT">COMPLAINT</option>
                  <option value="REFUND_REQUEST">REFUND_REQUEST</option>
                </select>
              </label>
              <label className="block text-xs text-clay-600">
                {tk('customerService.subject')}
                <input
                  name="subject"
                  required
                  maxLength={200}
                  className="mt-1 w-full rounded-xl border border-clay-200 bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs text-clay-600">
                {tk('customerService.ticketBody')}
                <textarea
                  name="body"
                  required
                  maxLength={5000}
                  rows={4}
                  className="mt-1 w-full rounded-xl border border-clay-200 bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs text-clay-600">
                orderId ({tk('common.optional')})
                <input name="orderId" className="mt-1 w-full rounded-xl border border-clay-200 bg-white px-3 py-2 text-sm font-mono text-xs" />
              </label>
              <label className="block text-xs text-clay-600">
                merchantId ({tk('common.optional')})
                <input name="merchantId" className="mt-1 w-full rounded-xl border border-clay-200 bg-white px-3 py-2 text-sm font-mono text-xs" />
              </label>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? tk('common.loading') : tk('customerService.submitTicket')}
              </Button>
            </form>

            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-clay-500">
                {tk('customerService.myTickets')} ({total})
              </h2>
              {items.length === 0 ? (
                <p className="mt-2 text-sm text-clay-500">{tk('customerService.empty')}</p>
              ) : (
                <ul className="mt-2 divide-y divide-clay-100 overflow-hidden rounded-2xl border border-clay-100 bg-white">
                  {items.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        className="w-full px-4 py-3 text-left transition hover:bg-clay-50"
                        onClick={() =>
                          navigate(
                            `/me/cs/conversations/${encodeURIComponent(t.conversationId)}?ticketId=${encodeURIComponent(t.id)}`,
                          )
                        }
                      >
                        <div className="font-mono text-[10px] text-clay-400">{t.id}</div>
                        <div className="text-sm font-medium text-clay-900">{t.subject}</div>
                        <div className="text-xs text-clay-500">
                          {t.kind} · {t.status}
                        </div>
                        <div className="mt-1 text-xs text-amber-700">{tk('customerService.openConversation')}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>

      <BottomNav />
    </main>
  );
}

function BackGlyph(): React.ReactElement {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
